import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS, normalizeLanguage } from '../locales/index.js';
import { stripLocalePrefix } from './localeRouting.js';

export const LOCALE_PREFERENCE_COOKIE = 'stocktag_locale';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const SUPPORTED_LANGUAGE_SET = new Set([
  DEFAULT_LANGUAGE,
  ...LANGUAGE_OPTIONS.map((option) => String(option.code || '').toLowerCase())
]);

const COUNTRY_LANGUAGE_MAP = new Map([
  ['US', 'en'],
  ['GB', 'en'],
  ['IE', 'en'],
  ['AU', 'en'],
  ['NZ', 'en'],
  ['TR', 'tr'],
  ['DE', 'de'],
  ['AT', 'de'],
  ['LI', 'de'],
  ['FR', 'fr'],
  ['IT', 'it'],
  ['SM', 'it'],
  ['VA', 'it'],
  ['ID', 'id'],
  ['TH', 'th'],
  ['BR', 'pt'],
  // India and Bangladesh are intentionally resolved from Accept-Language instead
  // of country: both are multilingual markets whose visitors largely search in
  // English (Search Console shows no Bengali-script queries from Bangladesh), so
  // a country-only Hindi or Bengali redirect would be too broad.
  ['ES', 'es'],
  ['MX', 'es'],
  ['AR', 'es'],
  ['CO', 'es'],
  ['CL', 'es'],
  ['PE', 'es'],
  ['UY', 'es'],
  ['EC', 'es'],
  ['BO', 'es'],
  ['PY', 'es'],
  ['CR', 'es'],
  ['PA', 'es'],
  ['GT', 'es'],
  ['HN', 'es'],
  ['NI', 'es'],
  ['SV', 'es'],
  ['DO', 'es'],
  ['CU', 'es'],
  ['VE', 'es'],
  ['GQ', 'es'],
  ['PR', 'es']
]);

const matchSupportedLanguage = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = String(value).trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGE_SET.has(normalized) ? normalized : null;
};

export const readLocalePreferenceFromCookieHeader = (cookieHeader = '') => {
  const escapedName = LOCALE_PREFERENCE_COOKIE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = String(cookieHeader || '').match(new RegExp(`(?:^|;\\s*)${escapedName}=([^;]+)`));
  if (!match?.[1]) return null;
  try {
    return matchSupportedLanguage(decodeURIComponent(match[1]));
  } catch {
    return matchSupportedLanguage(match[1]);
  }
};

export const readLocalePreferenceFromBrowser = () => {
  if (typeof document === 'undefined') return null;
  return readLocalePreferenceFromCookieHeader(document.cookie || '');
};

const toComparablePath = (value = '/') => {
  const { locale, pathname } = stripLocalePrefix(value);
  return { locale, pathname: pathname === '/index.html' ? '/' : pathname };
};

// True when the visitor followed a link from one language version of a page to
// another language version of the same page on this site, for example the
// language alternates nav from /tr/pricing to /pricing. That click is an explicit
// language choice, so neither the entry redirect nor the client auto-switch may
// send the visitor back to the language they just left.
export const isLanguageAlternateNavigation = ({ referrer = '', pathname = '/', host = '' } = {}) => {
  if (!referrer || !host) return false;
  let referrerUrl;
  try {
    referrerUrl = new URL(String(referrer));
  } catch {
    return false;
  }
  if (referrerUrl.host.toLowerCase() !== String(host).trim().toLowerCase()) return false;
  const from = toComparablePath(referrerUrl.pathname);
  const to = toComparablePath(pathname);
  return from.pathname === to.pathname && from.locale !== to.locale;
};

const getBrowserLanguagePreference = (browserLanguages = []) => (
  (Array.isArray(browserLanguages) ? browserLanguages : [browserLanguages])
    .map((value) => matchSupportedLanguage(value))
    .find(Boolean) || null
);

// Client mirror of resolveEntryLanguage. The URL's own locale prefix wins, then an
// explicit choice (a click between language versions of the page, the cookie or
// the saved UI language), then the browser's languages. The Istanbul time zone
// stands in for the edge country signal, so like the country it only applies
// when the browser names no language the site supports: an English browser in
// Istanbul stays in English.
export const resolveClientLanguagePreference = ({
  pathname = '/',
  cookieHeader = '',
  storedLanguage = '',
  browserLanguages = [],
  timeZone = '',
  referrer = '',
  host = ''
} = {}) => {
  const firstPathSegment = String(pathname || '/')
    .split(/[?#]/, 1)[0]
    .split('/')
    .filter(Boolean)[0] || '';
  const routeLanguage = matchSupportedLanguage(firstPathSegment);
  if (routeLanguage) return routeLanguage;

  if (isLanguageAlternateNavigation({ referrer, pathname, host })) return DEFAULT_LANGUAGE;

  const cookieLanguage = readLocalePreferenceFromCookieHeader(cookieHeader);
  if (cookieLanguage) return cookieLanguage;

  const savedLanguage = matchSupportedLanguage(storedLanguage);
  if (savedLanguage) return savedLanguage;

  const browserLanguage = getBrowserLanguagePreference(browserLanguages);
  if (browserLanguage) return browserLanguage;

  // The site Worker supplies country data in production. Istanbul is the only reliable
  // client-side country hint we use when that edge signal is unavailable
  // (local development, static mirrors, or privacy-restricted browsers).
  if (String(timeZone || '').trim() === 'Europe/Istanbul') return 'tr';

  return DEFAULT_LANGUAGE;
};

export const buildLocalePreferenceCookie = (language) => (
  `${LOCALE_PREFERENCE_COOKIE}=${encodeURIComponent(normalizeLanguage(language))}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
);

export const writeLocalePreferenceCookie = (language) => {
  if (typeof document === 'undefined') return;
  document.cookie = buildLocalePreferenceCookie(language);
};

export const getCountryFallbackLanguage = (countryCode = '') => {
  const normalizedCountry = String(countryCode || '').trim().toUpperCase();
  return COUNTRY_LANGUAGE_MAP.get(normalizedCountry) || null;
};

export const getAcceptLanguagePreference = (headerValue = '') => {
  const parsed = String(headerValue || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [tag, ...params] = part.split(';').map((item) => item.trim());
      const qParam = params.find((item) => item.startsWith('q='));
      const weight = qParam ? Number.parseFloat(qParam.slice(2)) : 1;
      return {
        language: matchSupportedLanguage(tag),
        weight: Number.isFinite(weight) ? weight : 1
      };
    })
    .filter((item) => item.language && item.weight > 0)
    .sort((left, right) => right.weight - left.weight);

  return parsed[0]?.language || null;
};

// Entry language for a visitor arriving on an unprefixed URL: an explicit choice
// (the cookie) first, then the browser's Accept-Language whenever it names any
// language the site supports, English included, so an "en-US,en" browser is never
// moved to a translation because of where it connects from. The IP country is
// only a fallback for browsers that name no supported language.
export const resolveEntryLanguage = ({
  cookieHeader = '',
  countryCode = '',
  acceptLanguage = ''
} = {}) => {
  const cookieLanguage = readLocalePreferenceFromCookieHeader(cookieHeader);
  if (cookieLanguage) return cookieLanguage;

  const acceptedLanguage = getAcceptLanguagePreference(acceptLanguage);
  if (acceptedLanguage) return acceptedLanguage;

  const countryLanguage = getCountryFallbackLanguage(countryCode);
  if (countryLanguage) return countryLanguage;

  return DEFAULT_LANGUAGE;
};
